import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardHeader,
  CardBody,
  Alert,
  AlertIcon,
  Spinner,
  Center,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react'
import {
  FiCalendar,
  FiBarChart,
  FiPieChart,
  FiActivity,
  FiMessageSquare,
  FiTag,
  FiEdit3,
} from 'react-icons/fi'
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { PageHeader } from '@/components/Common'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { useAdminGuard } from '@/hooks/useAdminGuard'
import { DashboardMetrics } from '@/types'
import apiService from '@/services/api'

// Color palette for charts
const COLORS = [
  '#3182CE',
  '#38A169',
  '#ED8936',
  '#9F7AEA',
  '#EF4444',
  '#06B6D4',
  '#F59E0B',
  '#EC4899',
]

export default function AdminDashboardPage() {
  const { t } = useAppTranslation('admin')
  const { isAuthorized, ForbiddenView } = useAdminGuard()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null)

  // Filter states
  const [selectedOrgId, setSelectedOrgId] = useState<string>('all')
  const [startDate, setStartDate] = useState(() => {
    const date = new Date()
    date.setDate(date.getDate() - 30) // Default to last 30 days
    return date.toISOString().split('T')[0]
  })
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0]
  })

  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      setError(null)

      const data = await apiService.getDashboardMetrics({
        startDate,
        endDate,
        organizationId: selectedOrgId,
      })

      setMetrics(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dashboard.fetchError'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboardData()
  }, [selectedOrgId, startDate, endDate])

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!metrics) return null

    const totalPages = metrics.pagesPerDay.reduce((sum, day) => sum + day.pages, 0)
    const totalFiles = metrics.filesByType.reduce((sum, type) => sum + type.count, 0)
    const totalQueries = metrics.chatbotQueriesPerDay.reduce((sum, day) => sum + day.queries, 0)
    const totalTagging = metrics.smartTaggingPerDay.reduce((sum, day) => sum + day.tagging, 0)
    const totalRenaming = metrics.smartTaggingPerDay.reduce((sum, day) => sum + day.renaming, 0)

    return {
      totalPages,
      totalFiles,
      totalQueries,
      totalTagging,
      totalRenaming,
    }
  }, [metrics])

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          bg={bgColor}
          p={3}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="md"
          boxShadow="lg"
        >
          <Text fontWeight="semibold" mb={1}>
            {label}
          </Text>
          {payload.map((entry: any, index: number) => (
            <Text key={index} color={entry.color} fontSize="sm">
              {entry.name || entry.dataKey}: {entry.value}
            </Text>
          ))}
        </Box>
      )
    }
    return null
  }

  // Access control
  if (!isAuthorized) return ForbiddenView

  if (loading) {
    return (
      <Center h="400px">
        <VStack spacing={4}>
          <Spinner size="xl" color="brand.500" />
          <Text color="gray.600">{t('dashboard.loading')}</Text>
        </VStack>
      </Center>
    )
  }

  if (error) {
    return (
      <Box p={{ base: 4, md: 6 }}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    )
  }

  return (
    <Box p={{ base: 4, md: 6 }}>
      <VStack spacing={8} align="stretch">
        <PageHeader
          title={t('dashboard.title')}
          description={t('dashboard.description')}
          icon={<FiBarChart />}
        >
          {/* Filters */}
          <HStack spacing={4} mt={4} flexWrap="wrap">
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={1}>
                {t('dashboard.filters.organization')}
              </Text>
              <Select
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                bg="white"
                minW="200px"
              >
                <option value="all">{t('dashboard.filters.allOrganizations')}</option>
                {metrics?.organizations.map((org) => (
                  <option key={org.id} value={org.id.toString()}>
                    {org.name}
                  </option>
                ))}
              </Select>
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={1}>
                {t('dashboard.filters.startDate')}
              </Text>
              <InputGroup>
                <InputLeftElement>
                  <FiCalendar color="gray.400" />
                </InputLeftElement>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  bg="white"
                />
              </InputGroup>
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={1}>
                {t('dashboard.filters.endDate')}
              </Text>
              <InputGroup>
                <InputLeftElement>
                  <FiCalendar color="gray.400" />
                </InputLeftElement>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  bg="white"
                />
              </InputGroup>
            </Box>
          </HStack>
        </PageHeader>

        {/* Summary Stats */}
        {summaryStats && (
          <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4}>
            <Stat bg="blue.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="blue.200">
              <StatLabel color="blue.600">
                <HStack>
                  <Icon as={FiActivity} />
                  <Text>{t('dashboard.stats.totalPages')}</Text>
                </HStack>
              </StatLabel>
              <StatNumber color="blue.800" fontSize="2xl">
                {summaryStats.totalPages.toLocaleString()}
              </StatNumber>
              <StatHelpText>{t('dashboard.stats.totalPagesHelp')}</StatHelpText>
            </Stat>

            <Stat bg="green.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="green.200">
              <StatLabel color="green.600">
                <HStack>
                  <Icon as={FiPieChart} />
                  <Text>{t('dashboard.stats.totalFiles')}</Text>
                </HStack>
              </StatLabel>
              <StatNumber color="green.800" fontSize="2xl">
                {summaryStats.totalFiles.toLocaleString()}
              </StatNumber>
              <StatHelpText>{t('dashboard.stats.totalFilesHelp')}</StatHelpText>
            </Stat>

            <Stat bg="purple.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="purple.200">
              <StatLabel color="purple.600">
                <HStack>
                  <Icon as={FiMessageSquare} />
                  <Text>{t('dashboard.stats.chatQueries')}</Text>
                </HStack>
              </StatLabel>
              <StatNumber color="purple.800" fontSize="2xl">
                {summaryStats.totalQueries.toLocaleString()}
              </StatNumber>
              <StatHelpText>{t('dashboard.stats.chatQueriesHelp')}</StatHelpText>
            </Stat>

            <Stat bg="orange.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="orange.200">
              <StatLabel color="orange.600">
                <HStack>
                  <Icon as={FiTag} />
                  <Text>{t('dashboard.stats.smartTagging')}</Text>
                </HStack>
              </StatLabel>
              <StatNumber color="orange.800" fontSize="2xl">
                {summaryStats.totalTagging.toLocaleString()}
              </StatNumber>
              <StatHelpText>{t('dashboard.stats.smartTaggingHelp')}</StatHelpText>
            </Stat>

            <Stat bg="cyan.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="cyan.200">
              <StatLabel color="cyan.600">
                <HStack>
                  <Icon as={FiEdit3} />
                  <Text>{t('dashboard.stats.smartRenaming')}</Text>
                </HStack>
              </StatLabel>
              <StatNumber color="cyan.800" fontSize="2xl">
                {summaryStats.totalRenaming.toLocaleString()}
              </StatNumber>
              <StatHelpText>{t('dashboard.stats.smartRenamingHelp')}</StatHelpText>
            </Stat>
          </SimpleGrid>
        )}

        {/* Charts Grid */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          {/* Pages Per Day Chart */}
          <Card
            borderWidth="1px"
            borderColor="blue.200"
            boxShadow="xl"
            _hover={{ boxShadow: '2xl', transform: 'translateY(-2px)' }}
            transition="all 0.3s ease"
          >
            <CardHeader>
              <HStack>
                <Icon as={FiActivity} color="blue.500" />
                <Text fontSize="lg" fontWeight="semibold">
                  {t('dashboard.charts.pagesPerDay')}
                </Text>
              </HStack>
            </CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={metrics?.pagesPerDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="pages"
                    name={t('dashboard.charts.series.pages')}
                    stroke="#3182CE"
                    fill="#3182CE"
                    fillOpacity={0.6}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>

          {/* Files By Type Chart */}
          <Card
            borderWidth="1px"
            borderColor="blue.200"
            boxShadow="xl"
            _hover={{ boxShadow: '2xl', transform: 'translateY(-2px)' }}
            transition="all 0.3s ease"
          >
            <CardHeader>
              <HStack>
                <Icon as={FiPieChart} color="green.500" />
                <Text fontSize="lg" fontWeight="semibold">
                  {t('dashboard.charts.filesByType')}
                </Text>
              </HStack>
            </CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={metrics?.filesByType}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                    nameKey="type"
                  >
                    {metrics?.filesByType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend
                    verticalAlign="bottom"
                    formatter={(value: string) => {
                      const count =
                        metrics?.filesByType.find((f) => f.type === value)?.count ?? 0
                      const total = summaryStats?.totalFiles ?? 0
                      const pct = total > 0 ? ((count / total) * 100).toFixed(0) : '0'
                      return `${value} ${pct}%`
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>

          {/* Chatbot Queries Per Day Chart */}
          <Card
            borderWidth="1px"
            borderColor="blue.200"
            boxShadow="xl"
            _hover={{ boxShadow: '2xl', transform: 'translateY(-2px)' }}
            transition="all 0.3s ease"
          >
            <CardHeader>
              <HStack>
                <Icon as={FiMessageSquare} color="purple.500" />
                <Text fontSize="lg" fontWeight="semibold">
                  {t('dashboard.charts.chatbotQueriesPerDay')}
                </Text>
              </HStack>
            </CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={metrics?.chatbotQueriesPerDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="queries"
                    name={t('dashboard.charts.series.queries')}
                    stroke="#9F7AEA"
                    strokeWidth={2}
                    dot={{ fill: '#9F7AEA' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>

          {/* Smart Operations Chart */}
          <Card
            borderWidth="1px"
            borderColor="blue.200"
            boxShadow="xl"
            _hover={{ boxShadow: '2xl', transform: 'translateY(-2px)' }}
            transition="all 0.3s ease"
          >
            <CardHeader>
              <HStack>
                <Icon as={FiTag} color="orange.500" />
                <Text fontSize="lg" fontWeight="semibold">
                  {t('dashboard.charts.smartOperationsPerDay')}
                </Text>
              </HStack>
            </CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={metrics?.smartTaggingPerDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="tagging" fill="#ED8936" name={t('dashboard.charts.series.tagging')} />
                  <Bar dataKey="renaming" fill="#F59E0B" name={t('dashboard.charts.series.renaming')} />
                </BarChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </SimpleGrid>
      </VStack>
    </Box>
  )
}
